import { useEffect, useState, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  Save,
  X,
  Monitor,
  Cpu,
  Network,
  AlertCircle,
  Info,
  CheckCircle2,
  Lock,
} from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import {
  get_restaurant_setting,
  save_restaurant_setting,
  server_post_data,
} from "../ServiceConnection/serviceconnection";

export default function ConfigDevice() {
  const { toast } = useToast();

  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    api_key: "",
    access_token: "",
  });

  const [originalData, setOriginalData] = useState({
    api_key: "",
    access_token: "",
  });

  const [hasChanges, setHasChanges] = useState(false);

  // MAC address regex: XX:XX:XX:XX:XX:XX or XX-XX-XX-XX-XX-XX
  const MAC_REGEX = /^([0-9A-Fa-f]{2}[:\-]){5}[0-9A-Fa-f]{2}$/;
  // IPv4 regex
  const IP_REGEX =
    /^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]\d|\d)(\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]\d|\d)){3}$/;

  const isFormValid =
    formData.api_key.trim() !== "" && formData.access_token.trim() !== "";

  useEffect(() => {
    setHasChanges(JSON.stringify(formData) !== JSON.stringify(originalData));
  }, [formData, originalData]);

  const handleFieldChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const { [field]: removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const fetchSettingsData = useCallback(async () => {
    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("flag", 1);
    fd.append("want_table_data", 1);
    fd.append("device_config", 1);

    try {
      const response = await server_post_data(get_restaurant_setting, fd);

      if (!response.data.error) {
        const data = response.data.message?.data_restaurant?.[0];
        if (data) {
          const fetched = {
            api_key: data.single_mac_id || "",
            access_token: data.single_local_url || "",
          };
          setFormData(fetched);
          setOriginalData(fetched);
          setHasChanges(false);
        }
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching device config:", error);
      toast({
        title: "Error",
        description: "Failed to load device configuration",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [toast]);

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.api_key.trim()) {
      newErrors.api_key = "MAC ID is required";
    } else if (!MAC_REGEX.test(formData.api_key.trim())) {
      newErrors.api_key = "Invalid MAC address format (e.g. AA:BB:CC:DD:EE:FF)";
    }

    if (!formData.access_token.trim()) {
      newErrors.access_token = "Static IP is required";
    } else if (!IP_REGEX.test(formData.access_token.trim())) {
      newErrors.access_token = "Invalid IP address format (e.g. 192.168.1.100)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSave = useCallback(async () => {
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors before saving",
        variant: "destructive",
      });
      return;
    }

    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("from_where", "9"); // TODO: confirm flag value with backend
    Object.keys(formData).forEach((key) => {
      fd.append(key, formData[key]);
    });

    try {
      const response = await server_post_data(save_restaurant_setting, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: "Device configuration saved successfully",
        });
        fetchSettingsData();
      }
    } catch (error) {
      console.error("Save error:", error);
      toast({
        title: "Error",
        description: "Failed to save device configuration",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [formData, validateForm, toast, fetchSettingsData]);

  const handleCancel = useCallback(() => {
    setFormData(originalData);
    setErrors({});
    setHasChanges(false);
  }, [originalData]);

  useEffect(() => {
    fetchSettingsData();
  }, [fetchSettingsData]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">
            Config Device
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Configure the MAC ID and Static IP of your primary device
          </p>
        </div>
      </div>

      {/* Configured Status */}
      {isFormValid && !hasChanges && (
        <Alert className="border-success/50 bg-success/10">
          <CheckCircle2 className="h-4 w-4 text-success" />
          <AlertDescription className="text-success">
            Primary device is configured and active
          </AlertDescription>
        </Alert>
      )}

      {/* Device Config Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Cpu className="w-4 h-4 text-primary" />
            Primary Device
          </CardTitle>
          <CardDescription>
            Enter the network details of the primary device for this outlet
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* MAC ID */}
          <div className="space-y-2">
            <Label>
              MAC ID <span className="text-destructive">*</span>
            </Label>
            <Input
              placeholder="e.g. AA:BB:CC:DD:EE:FF"
              value={formData.api_key}
              onChange={(e) => handleFieldChange("api_key", e.target.value)}
              className={errors.api_key ? "border-destructive" : ""}
              maxLength={17}
            />
            {errors.api_key ? (
              <p className="text-xs text-destructive flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.api_key}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Format: AA:BB:CC:DD:EE:FF or AA-BB-CC-DD-EE-FF
              </p>
            )}
          </div>

          {/* Static IP */}
          <div className="space-y-2">
            <Label>
              Static IP <span className="text-destructive">*</span>
            </Label>
            <Input
              placeholder="e.g. 192.168.1.100"
              value={formData.access_token}
              onChange={(e) => handleFieldChange("access_token", e.target.value)}
              className={errors.access_token ? "border-destructive" : ""}
            />
            {errors.access_token ? (
              <p className="text-xs text-destructive flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.access_token}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Format: 192.168.x.x (IPv4 address)
              </p>
            )}
          </div>

          {/* Unsaved Changes Warning */}
          {hasChanges && isFormValid && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-warning/10 border border-warning/20">
              <Lock className="w-4 h-4 text-warning" />
              <p className="text-sm text-warning">
                You have unsaved changes. Click Save to apply.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Network className="w-5 h-5 text-primary" />
            </div>
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-foreground">
                About Device Configuration
              </h4>
              <p className="text-sm text-muted-foreground">
                The primary device configuration is used to identify and
                communicate with the main billing or KOT terminal on your local
                network.
              </p>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside mt-2">
                <li>MAC ID uniquely identifies the device on the network</li>
                <li>Static IP ensures consistent connectivity</li>
                <li>Used for printer routing and order dispatch</li>
              </ul>
              <Alert className="mt-3">
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Ensure the device has a reserved IP in your router's DHCP
                  settings to prevent IP conflicts
                </AlertDescription>
              </Alert>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-4 pt-4 border-t">
        <Button
          variant="outline"
          onClick={handleCancel}
          disabled={showLoaderAdmin || !hasChanges}
          className="gap-2"
        >
          <X className="w-4 h-4" />
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={showLoaderAdmin || !hasChanges || !isFormValid}
          className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
        >
          {showLoaderAdmin ? (
            "Saving..."
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Configuration
            </>
          )}
        </Button>
      </div>
    </div>
  );
}